import { ExchangeForm } from '@exodus/exchange-ui';
import type { ComponentProps } from '@exodus/exchange-ui/lib/form/components/to-asset-input/index.js';
import type { ComponentType } from 'react';

import { cn } from '@/ui/utils/classnames.js';
import type { AssetClickHandler, SwapFormVariant } from '../types.js';
import { pickFirstTruthy } from '../utils.js';
import AssetButton, {
  AssetButtonSkeleton,
  AssetButtonWithoutText,
  DisabledAssetButton,
  DisabledAssetButtonWithoutText,
} from './asset-button.js';
import AssetInput, { DisabledAssetInput } from './asset-input.js';
import FiatAssetInput, { DisabledFiatAssetInput } from './fiat-asset-input.js';
import Label, { LabelSkeleton } from './label.js';
import ToLayout from './to-layout.js';

type ToFieldsProps = {
  variant: SwapFormVariant;
  disabled?: boolean;
  onAssetClick: AssetClickHandler;
};

function ToFields({ variant, disabled, onAssetClick }: ToFieldsProps) {
  const isHorizontal = variant === 'horizontal';
  const isVertical = variant === 'vertical';

  return (
    <ToLayout
      variant={variant}
      header={
        <>
          <ExchangeForm.ToAssetLabel
            Component={Label}
            className={cn('flex-1', isHorizontal && 'text-right')}
          />

          {isVertical && (
            <ExchangeForm.ToAssetInput
              mode="fiat"
              Component={
                // FIXME: Temporary hack to cope with incorrect type
                DisabledFiatAssetInput as unknown as ComponentType<ComponentProps>
              }
            />
          )}
        </>
      }
    >
      <ExchangeForm.ToAsset
        Component={pickFirstTruthy(
          isHorizontal && disabled && DisabledAssetButtonWithoutText,
          isHorizontal && AssetButtonWithoutText,
          disabled && DisabledAssetButton,
          AssetButton,
        )}
        className={cn(
          isHorizontal && 'col-start-2',
          isVertical && 'col-start-1',
        )}
        onPress={onAssetClick}
      />

      <ExchangeForm.ToAssetInput
        Component={
          // FIXME: Temporary hack to cope with incorrect type
          DisabledAssetInput as unknown as ComponentType<ComponentProps>
        }
        className={cn('flex flex-1 justify-end', isHorizontal && 'row-start-1')}
      />

      {isHorizontal && (
        <ExchangeForm.ToAssetInput
          mode="fiat"
          Component={
            // FIXME: Temporary hack to cope with incorrect type
            DisabledFiatAssetInput as unknown as ComponentType<ComponentProps>
          }
          className="flex justify-end"
        />
      )}
    </ToLayout>
  );
}

export function ToFieldsSkeleton({ variant }: Pick<ToFieldsProps, 'variant'>) {
  const isHorizontal = variant === 'horizontal';
  const isVertical = variant === 'vertical';

  return (
    <ToLayout
      variant={variant}
      header={
        <>
          <LabelSkeleton
            className={cn('flex flex-1', isHorizontal && 'justify-end')}
          />

          {isVertical && (
            <div className="flex w-1/2 justify-end">
              <FiatAssetInput loading />
            </div>
          )}
        </>
      }
    >
      <AssetButtonSkeleton
        noText={isHorizontal}
        className={cn(
          isHorizontal && 'col-start-2',
          isVertical && 'col-start-1',
        )}
      />

      <AssetInput
        className={cn('flex flex-1 justify-end', isHorizontal && 'row-start-1')}
        loading
      />

      {isHorizontal && <FiatAssetInput loading className="flex justify-end" />}
    </ToLayout>
  );
}

export default ToFields;
